import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import ContentSection from '../../../ContentSection';
import {
    createBetCard,
    getMarketHeader,
    createDescriptionTemplate,
    showWidget,
} from '../../../../../utils/widgets';
import {
    getTodayTomorrowDateLabel,
} from '../../../../../utils/date-time';
import {
    FIRST_ELEMENT_KEY,
    GTM_SEE_ALL_MATCHES,
} from '../../constants';
import {
    TODAY_STRING,
    TOMORROW_STRING,
    FEATURED_MATCHES_ICON,
    TODAY_I18N_KEY,
    TOMORROW_I18N_KEY,
    GTM_FEATURED_MATCHES,
    GTM_SEE_ALL_PREFIX,
} from '../../../../../utils/constants';
import WidgetContainer from '../../../../../ui/WidgetContainer';
import i18n from '../../../../../utils/i18n';
import { seoUrlSportToday, seoUrlSportTomorrow, seoUrlTournament } from '../../../../../../spa/Utils/SeoUrls';
import { getSportIdBySlug, getHasBoostedOddsSelections } from '~shared/utils/sportsbook';

export default class FeaturedMatchesLayout extends React.Component {
    constructor() {
        super();

        this.getDateSectionHeaderFooter = this.getDateSectionHeaderFooter.bind(this);
        this.getTournamentSectionHeaderFooter = this.getTournamentSectionHeaderFooter.bind(this);
        this.getContentSection = this.getContentSection.bind(this);
        this.getEmptyContentSection = this.getEmptyContentSection.bind(this);
        this.createContentSection = this.createContentSection.bind(this);
    }

    static propTypes = {
        sport: PropTypes.string,
        widgetHeader: PropTypes.string,
        events: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.array,
        ]),
        commonDataEvents: PropTypes.object,
        source: PropTypes.string,
    };

    /**
     * Get the parameters for the Content Section component
     * @param {String} dateLabel 'Today' or 'Tomorrow'
     * @param {Array} defaultMarkets Array of default markets
     * @param {Number} sportId The sport ID
     * @return {Object} Content Section Params: headerText, isCollapsible and titleModifiers.
     */
    getDateSectionHeaderFooter(dateLabel, defaultMarkets, sportId) {
        const day = dateLabel.toLocaleLowerCase();
        const gtmAttribute = `${this.props.source};${GTM_FEATURED_MATCHES};${GTM_SEE_ALL_PREFIX}${day}`;
        const translatedDayLabel = i18n.t(`football.${day}`);
        const translatedDayUrl = i18n.t(`url.${day}`);

        let footerLink = `/${i18n.t('url.football')}/${translatedDayUrl}`;

        switch (day) {
            case 'today':
                footerLink = seoUrlSportToday(footerLink, sportId);
                break;
            case 'tomorrow':
                footerLink = seoUrlSportTomorrow(footerLink, sportId);
                break;
            default:
        }

        return {
            headerText: getMarketHeader(this.props.sport, translatedDayLabel, defaultMarkets),
            isCollapsible: false,
            titleModifiers: ['column-headers'],
            footerText: i18n.t('football.view_all_[date_label]_matches', { dateLabel: translatedDayLabel }),
            footerLink,
            gtmAttribute,
        };
    }

    /**
     * Get the parameters for the Content Section component
     * @param {String} label Tournament name
     * @param {String} category_slug Category slug
     * @param {String} tournament_slug Tournament slug
     * @param {String} tournament_id The tournament id
     * @return {Object} Content Section Params: headerText, isCollapsible and titleModifiers.
     */
    getTournamentSectionHeaderFooter(label, category_slug, tournament_slug, tournament_id) {

        const tournamentLink = seoUrlTournament(`/${i18n.t('url.football')}/${category_slug}/${tournament_slug}`, tournament_id);
        const gtmAttribute = `${this.props.sport};${GTM_FEATURED_MATCHES};${GTM_SEE_ALL_MATCHES}`;

        return {
            headerText: label,
            isCollapsible: false,
            titleModifiers: ['label-headers'],
            footerText: i18n.t('football.view_all_[label]', { label }),
            footerLink: tournamentLink,
            footerLinkModifiers: ['tournament-link'],
            isTournament: true,
            gtmAttribute,
        };
    }

    /**
     * Get the Content Section component grouped by date
     * @param {String} dateLabel 'Today' or 'Tomorrow'
     * @param {Object} events List of events
     * @param {Object} commonEvents List of events in Common Data Store
     * @param {Object} defaultMarkets List of default markets
     * @param {Int} defaultMarketId Default market id
     * @return {ContentSection} The Content Section element
     */
    getContentSection(dateLabel, events, commonEvents, defaultMarkets, defaultMarketId) {
        const gtmAttribute = `${this.props.source};${GTM_FEATURED_MATCHES};`;

        return (
            // date section
            <ContentSection params={this.getDateSectionHeaderFooter(dateLabel, defaultMarkets, getSportIdBySlug(this.props.sport))}>
                {events.map((eventsSection, key) => {
                    return(
                        // tournament section
                        <ContentSection key={key} params={
                            this.getTournamentSectionHeaderFooter(
                                eventsSection.label,
                                eventsSection.category_slug,
                                eventsSection.tournament_slug,
                                eventsSection.tournament_id)}>
                            {// events list
                                eventsSection.events.map((event, key) => {
                                    const eventObj = commonEvents[event.event_id];
                                    const defaultMarket = eventObj.markets[defaultMarketId];
                                    const hasBoostedOddsSelections = Boolean(defaultMarket) && getHasBoostedOddsSelections(defaultMarket.selections);
                                    const descriptionTemplate = createDescriptionTemplate(eventObj, false, gtmAttribute, hasBoostedOddsSelections);

                                    return createBetCard(
                                        eventObj,
                                        defaultMarketId,
                                        key,
                                        descriptionTemplate,
                                        gtmAttribute,
                                    );
                                })
                            }
                        </ContentSection>
                    );
                })}
            </ContentSection>
        );
    }

    /**
     * Create an empty Contect Section layout
     * @param {String} dateLabel 'Today' or 'Tomorrow'
     * @param {Number} sportId The sport ID
     * @return {ContentSection} The Content Section element
     */
    getEmptyContentSection(dateLabel, sportId) {
        const day = dateLabel.toLocaleLowerCase();
        const gtmAttribute = `${this.props.source};${GTM_FEATURED_MATCHES};${GTM_SEE_ALL_PREFIX}${day}`;
        const translatedDayUrl = i18n.t(`url.${day}`);

        let footerLink = `/${i18n.t('url.football')}/${translatedDayUrl}`;

        switch (day) {
            case 'today':
                footerLink = seoUrlSportToday(footerLink, sportId);
                break;
            case 'tomorrow':
                footerLink = seoUrlSportTomorrow(footerLink, sportId);
                break;
            default:
        }

        return (
            <ContentSection params={{
                footerText: i18n.t('football.view_all_[date_label]_matches', { dateLabel: i18n.t(`football.${day}`)}),
                footerLink,
                gtmAttribute,
            }}></ContentSection>
        );
    }

    /**
     * Create a ContentSection element containing the list of events in the specified group
     * @param {Object} events The object with events data
     * @return {ContentSection} The ContentSection element
     */
    createContentSection(events) {
        // get events/markets ids and isolate the first of each
        let firstEvent = events[FIRST_ELEMENT_KEY];
        // common events info and sport slug
        const {
            commonDataEvents: commonEvents,
        } = this.props;
        // match state event ids with commonData event ids
        const commonEventsKeys = Object.keys(commonEvents);
        // widget events
        const widgetEvents = {
            today: [],
            tomorrow: [],
        };
        const todayI18n = i18n.t(TODAY_I18N_KEY);
        const tomorrowI18n = i18n.t(TOMORROW_I18N_KEY);
        events.forEach((event) => {
            if (commonEventsKeys.includes(String(event.event_id))) {
                // events/sections info
                firstEvent = event;
                const commonEventInfo = commonEvents[event.event_id];
                const dateLabel = getTodayTomorrowDateLabel(commonEventInfo.start_time);
                let dateGroup = '';
                switch (dateLabel) {
                    case todayI18n:
                        dateGroup = 'today';
                        break;
                    case tomorrowI18n:
                        dateGroup = 'tomorrow';
                        break;
                    default:
                        return;
                 }
                 let tournament = widgetEvents[dateGroup].find(
                    ({ tournament_id }) => tournament_id === commonEventInfo.tournament_id
                );
                if (!tournament) {
                    const tournamentLabel = commonEventInfo.tournament_display_name
                        ? commonEventInfo.tournament_display_name
                        : `${commonEventInfo.tournament_name}, ${commonEventInfo.category_name}`;
                    const categorySlug = commonEventInfo.category_slug_i18n ? commonEventInfo.category_slug_i18n : commonEventInfo.category_slug;
                    const tournamentSlug = commonEventInfo.tournament_slug_i18n ? commonEventInfo.tournament_slug_i18n : commonEventInfo.tournament_slug;

                    tournament = {
                        label: tournamentLabel,
                        category_slug: categorySlug,
                        tournament_slug: tournamentSlug,
                        tournament_id: commonEventInfo.tournament_id,
                        events: [],
                    };
                    widgetEvents[dateGroup].push(tournament);
                }
                tournament.events.push(event);
            }
        });

        // get markets ids
        const defaultMarketId = firstEvent.market_id;

        // get default markets for the selection columns
        const defaultMarkets = commonEvents[firstEvent.event_id].markets[defaultMarketId];

        return (
            <div>
                {!isEmpty(widgetEvents.today) ? (
                    this.getContentSection(TODAY_STRING, widgetEvents.today, commonEvents, defaultMarkets, defaultMarketId)
                ) : (
                    this.getEmptyContentSection(TODAY_STRING, getSportIdBySlug(this.props.sport))
                )}
                {!isEmpty(widgetEvents.tomorrow) ? (
                    this.getContentSection(TOMORROW_STRING, widgetEvents.tomorrow, commonEvents, defaultMarkets, defaultMarketId)
                ) : (
                    this.getEmptyContentSection(TOMORROW_STRING, getSportIdBySlug(this.props.sport))
                )}
            </div>
        );
    }

    /**
     * Render the component
     * @returns {JSX.Element} Returns a React Element
     */
    render() {

        return(
            <div>
                <WidgetContainer
                    title={this.props.widgetHeader}
                    titleIconClassName={FEATURED_MATCHES_ICON}>
                    {showWidget(this.props.events, this.props.commonDataEvents) ?
                    (
                        <div>
                            {this.createContentSection(this.props.events)}
                        </div>
                    ) : (
                        <div>
                            {this.getEmptyContentSection(TODAY_STRING, getSportIdBySlug(this.props.sport))}
                            {this.getEmptyContentSection(TOMORROW_STRING, getSportIdBySlug(this.props.sport))}
                        </div>
                    )}
                </WidgetContainer>
            </div>
        );
    }
}
